import "./Select.scss";

import { Autocomplete as AC, TextField, Chip, Tooltip, IconButton, InputAdornment } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FunctionComponent } from "react";
import { SelectProps } from "./ISelectProps";
import { Controller, useFormContext } from "react-hook-form";

const Select: FunctionComponent<SelectProps> = (props) => {
  const {
    multiple = false,
    options = [],
    value,
    inputValue,
    onChange,
    name,
    label,
    placeholder,
    InputRef,
    disabled,
    loading,
    freeSolo,
    disableClearable,
    disableCloseOnSelect,
    filterSelectedOptions,
    autoHighlight,
    noOptionsText,
    getOptionDisabled,
    getOptionLabel,
    onOpen,
    onClose,
    onFocus,
    onBlur,
    onInputChange,
    onKeyDown,
    variant,
    className,
    inputSx,
    inputLableStyle,
    size = "medium",
    labelSize,
    readOnly,
    fullWidth,
    sx,
    required,
    infoTooltip,
    infoIconSx,
    tooltipSx,
    disablePortal = false,
  } = props;

  const { control, formState: { errors } } = useFormContext();

  return (
    <Controller
      name={name!}
      control={control}
      render={({ field }) => (
        <AC
          {...field}
          multiple={multiple}
          options={options}
          value={value}
          inputValue={inputValue}
          disablePortal={disablePortal}
          disabled={disabled}
          loading={loading}
          freeSolo={freeSolo}
          fullWidth={fullWidth}
          noOptionsText={noOptionsText}
          disableClearable={disableClearable}
          filterSelectedOptions={filterSelectedOptions}
          autoHighlight={autoHighlight}
          readOnly={readOnly}
          sx={sx}
          size={size}

          // ⭐ FIXED: Compare value safely
          isOptionEqualToValue={(o, v) => {
            if (!o || !v) return false;

            const oVal = typeof o === "string" ? o : o.value;
            const vVal = typeof v === "string" ? v : v.value;

            return oVal === vVal;
          }}

          getOptionDisabled={getOptionDisabled}
          getOptionLabel={(opt) =>
            getOptionLabel
              ? getOptionLabel(opt)
              : typeof opt === "string"
                ? opt
                : opt?.label || ""
          }

          // ⭐ FIXED: Handle single + multiple values safely
          onChange={(event, newValue, reason) => {
            // Only call field.onChange if no parent onChange is provided, 
            // or if we want to ensure the value is mapped correctly.
            // To avoid breaking other things, we'll just map to .value if it exists.
            const mappedValue = multiple
              ? (newValue as any[])?.map((v) => (typeof v === "object" && v !== null ? v.value ?? v : v))
              : (typeof newValue === "object" && newValue !== null ? (newValue as any).value ?? newValue : newValue);

            field.onChange(mappedValue);

            if (onChange) {
              onChange(event, newValue as any, reason);
            }
          }}

          onOpen={onOpen}
          onClose={onClose}
          onFocus={onFocus}
          onBlur={onBlur}
          onInputChange={onInputChange}
          onKeyDown={onKeyDown}

          // ⭐ Chips only for multiple
          renderTags={(selected, getTagProps) =>
            multiple
              ? selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={index}
                  size="small"
                  label={
                    typeof option === "string"
                      ? option
                      : option?.label || ""
                  }
                />
              ))
              : null
          }

          renderOption={(props, option) => (
            <li {...props}>
              <div className="truncate-option">
                {typeof option === "string" ? option : option?.label}
              </div>
            </li>
          )}

          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={InputRef}
              label={label}
              placeholder={placeholder}
              variant={variant}
              required={required}
              error={!!errors[name!]}
              helperText={errors[name!]?.message?.toString()}
              className={`GlobalInputStyle ${className}`}
              sx={inputSx}
              InputLabelProps={{
                shrink: true,
                size: labelSize,
                className: `GlobalInputLableStyle ${inputLableStyle}`,
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {infoTooltip && (
                      <InputAdornment position="end">
                        <Tooltip
                          title={infoTooltip}
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '1rem', // Default small font
                                ...tooltipSx
                              }
                            }
                          }}
                        >
                          <IconButton
                            size="small"
                            tabIndex={-1}
                            sx={{ p: 0, mr: 0.5 }}
                            aria-label={`${label} info`}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                fontSize: '1.5rem', // Default size
                                color: 'action.active',
                                ...infoIconSx
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default Select;
